import { HttpService } from 'core/modules/network-service';
import { get, merge } from 'lodash';

// local dependencies
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getStore } from 'store';
import { REDUCER, MEMBER_SEARCH_MODULE } from 'constants/entity-names';
import { isInbound } from 'resolvers/callStateEntities';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc AuthFlowCallerIdentificationThirdParty defines an HTTP service interface to send HTTP network requests.
 * @example
 * import { AuthFlowCallerIdentificationThirdPartyService } from 'services/authFlowCallerIdentificationThirdParty';
 *
 * // create service instance and send/cancel request
 * const authFlowCallerIdentificationThirdPartyService = new AuthFlowCallerIdentificationThirdPartyService();
 * const result = await authFlowCallerIdentificationThirdPartyService.fetchData( '<user-id>' ); // fetch data
 * authFlowCallerIdentificationThirdPartyService.cancel(); // cancel an active request
 */
export class AuthFlowCallerIdentificationThirdPartyService extends HttpService {

    // make request and fetch data
    async fetchComponentData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                        module
                    },
                } ),
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    sections: get( response, 'body.component.structure.sections', {} ),
                    options: get( response, 'body.component.data.options', {} ),
                    actions: get( response, 'body.component.structure.actions', [] )
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // add or update title/Description item
	async addUpdateItem( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;

        // extract `operation` and actual payload
        const { operation, title, firstName, lastName } = data;

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        callType,
                        operation,
                        module
                    },
                    data: {
                        title,
                        firstName,
                        lastName
                    },
                }
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    title,
                    firstName,
                    lastName
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // register inbound caller (member)
    async registerInboundCaller( { url = {}, data = {}, callerType = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = isInbound() ? MEMBER_SEARCH_MODULE.CALLER_IDENTIFICATION: MEMBER_SEARCH_MODULE.CALLEE_IDENTIFICATION;
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {
                        callId,
                        callType,
                        module,
                        callerType
                    },
                    data: data,
                },
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: null,
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // format error response
    _errorFormatter( errResponse ) {
        return errResponse; // some logic
    }
}
